import { Placement } from '@popperjs/core';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef } from 'react';
import { ImageRenderer } from 'src/common/ImageRenderer';
import { Content } from 'src/common/interfaces/content.interface';
import { Node } from 'src/common/interfaces/node.interface';
import { DEFAULT_ANIMATION_DURATION, Popover } from 'src/common/ui/popover';
import { useHover } from 'src/common/use-hover.hook';
import { ContentTypeLogo } from 'src/dashboard/ContentTypeLogo';
import { DocumentViewerFactory } from 'src/document-editor/viewer.factory';
import { PreviewStrongIcon } from 'src/monet/icons';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';
import { useImageAssetSrcSet } from 'src/utils/use-image-asset-src-set.hook';
import { ImageWithHoverableIcon } from '../image-with-hoverable-icon';

export interface ContentCardWithPreviewProps {
  content: Content | Node;
  children: (name: string) => JSX.Element;
  containerClassName?: string;
  placement?: Placement;
  alignment?: 'center' | 'top';
  previewSize?: 'default' | 'small';
  onPlacementChange?: (placement: Placement) => void;
  iconWrapperClassName?: string;
  onHover?: () => void;
}

const PREVIEW_IMAGE_HEIGHT_WITH_PADDING = 214;

export const ContentCardWithPreview = ({
  content,
  children,
  placement,
  containerClassName,
  previewSize = 'default',
  alignment = 'top',
  iconWrapperClassName,
  onPlacementChange = functionNoop,
  onHover = functionNoop,
}: ContentCardWithPreviewProps) => {
  const previewImageContainerElementRef = useRef<Nullable<HTMLImageElement>>(null);
  const originElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const panelElementRef = useRef<Nullable<HTMLDivElement>>(null);

  const thumbnailSrcSet = useImageAssetSrcSet(content?.thumbnail || undefined, 'thumbnail_300x300');

  const hovering = useHover(originElementRef);

  const previewOpenRef = useRef(functionNoop);
  const previewCloseRef = useRef(functionNoop);

  useEffect(() => {
    const calculatePreviewPlacement = () => {
      const { current: originElement } = originElementRef;
      if (originElement) {
        const { bottom: originBottom } = originElement.getBoundingClientRect();
        let previewPosition: Placement = 'bottom';
        if (originBottom + PREVIEW_IMAGE_HEIGHT_WITH_PADDING > window.innerHeight) {
          previewPosition = 'top';
        }
        onPlacementChange(previewPosition);
      }
    };

    const dismissThumbnail = () => {
      previewCloseRef.current();
    };

    const showThumbnailWithDebounce = debounce(() => {
      previewOpenRef.current();
    }, DEFAULT_ANIMATION_DURATION);

    if (hovering) {
      calculatePreviewPlacement();
      showThumbnailWithDebounce();
      onHover();
    } else {
      dismissThumbnail();
    }
  }, [hovering]);

  const contentPreviewIsStaticImage = !!content.thumbnail?.url;
  const contentHasPreviewThumbnail = contentPreviewIsStaticImage || !!content.document;

  const renderDocument = DocumentViewerFactory(content);

  return (
    <div className={classNames('relative flex space-x-2', containerClassName)}>
      {!contentHasPreviewThumbnail ? (
        <>
          <ContentTypeLogo
            destination={content.embed?.destination}
            externalLinkUrl={content.external_link?.url}
            contentType={content.content_type}
          />
        </>
      ) : (
        <Popover
          offset={[-20, 8]}
          placement={placement || 'bottom'}
          open={hovering}
          openRef={previewOpenRef}
          popperClassName='z-50'
          popoverClassName='static'
          positioningStrategy='fixed'
          containerClassName='border border-bedrock-gray-medium p-4'
          closeRef={previewCloseRef}
          panelRef={panelElementRef}
          renderOrigin={() => (
            <ImageWithHoverableIcon
              content={content}
              ref={originElementRef}
              iconWrapperClassName={classNames({ 'm-auto': alignment === 'center' }, iconWrapperClassName)}
              iconContainerClassName={classNames({
                'items-center': alignment === 'center',
                'items-start': alignment === 'top',
              })}
              renderIcon={() => <PreviewStrongIcon className='w-5 h-full m-auto text-bedrock-white' />}
            />
          )}
        >
          <div
            className={classNames('relative h-[11.25rem] overflow-hidden', {
              'w-80': previewSize === 'default',
              'w-60': previewSize === 'small',
            })}
            ref={previewImageContainerElementRef}
          >
            <div className='absolute inset-0 bg-bedrock-black-overlay rounded-md opacity-100 transition ease-in' />
            {contentPreviewIsStaticImage ? (
              <ImageRenderer
                src={content.thumbnail!.url}
                srcSet={thumbnailSrcSet}
                className='rounded object-contain m-auto'
                loadingClassName='bg-bedrock-white rounded-md flex items-center justify-center w-full h-full'
                showLoading
                showSpinnerOnLoading
              />
            ) : (
              <div className='relative'>
                <div className='absolute inset-0 bg-bedrock-black-overlay rounded-md opacity-100 transition ease-in z-40' />
                {renderDocument()}
              </div>
            )}
          </div>
        </Popover>
      )}
      {children(content.name)}
    </div>
  );
};
